/**
 * Admin Contracts Page
 *
 * Director edits contract documents (Google Drive):
 * - the 3 per-type defaults (individual / duo / group), and
 * - per-class overrides that replace the type default for one class only.
 *
 * The list stays compact: rows are collapsed to one line and searchable;
 * editing a drive link opens inline, one row at a time. Saving a new link
 * bumps the version and forces the affected students (a whole type, or just
 * one class) to sign again.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SearchableSelect, Spinner } from "../../components/ui";
import type { SearchableSelectItem } from "../../components/ui";
import {
  useAdminClassGroups,
  useAdminContracts,
  useCreateContractOverride,
  useDeleteContractOverride,
  useUpdateContract,
} from "../../hooks/useAdmin";
import type { AdminContract } from "../../types/admin";

const TYPE_LABEL: Record<string, string> = {
  individual: "Individual",
  duo: "Duo",
  group: "Group",
};

/** Accept a full Drive URL or a raw file id; return the file id. */
function extractDriveFileId(input: string): string {
  const match = input.match(/\/d\/([a-zA-Z0-9_-]+)/);
  return match ? match[1] : input.trim();
}

/** "Updated Jul 1, 2026" — last time the document was changed. */
function formatUpdated(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function AdminContractsPage() {
  const { data: contracts, isLoading } = useAdminContracts();
  const { data: classes } = useAdminClassGroups();
  const createOverride = useCreateContractOverride();

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newClass, setNewClass] = useState("");
  const [newFile, setNewFile] = useState("");

  const defaults = useMemo(
    () => (contracts ?? []).filter((c) => !c.classGroup),
    [contracts],
  );
  const overrides = useMemo(
    () => (contracts ?? []).filter((c) => c.classGroup),
    [contracts],
  );
  const overrideTotal = overrides.length;

  // Classes without an override yet (for the add picker).
  const overrideClassIds = useMemo(
    () => new Set((contracts ?? []).filter((c) => c.classGroup).map((c) => c.classGroup)),
    [contracts],
  );
  const classItems = useMemo<SearchableSelectItem[]>(
    () =>
      (classes ?? [])
        .filter((c) => c.isActive && !overrideClassIds.has(c.id))
        .map((c) => ({
          id: c.id,
          label: `${c.name} (${c.levelCode} · ${TYPE_LABEL[c.classType]})`,
          searchText: `${c.name} ${c.levelCode} ${c.classType}`,
        })),
    [classes, overrideClassIds],
  );

  const toggle = (id: string) => setExpandedId((cur) => (cur === id ? null : id));

  const handleCreate = () => {
    const fileId = extractDriveFileId(newFile);
    if (!newClass || !fileId) return;
    createOverride.mutate(
      { classGroup: newClass, driveFileId: fileId },
      {
        onSuccess: () => {
          setNewClass("");
          setNewFile("");
          setAddOpen(false);
        },
      },
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/admin"
            className="inline-flex items-center gap-1 text-earth-700 hover:text-earth-700 text-sm mb-2 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Dashboard
          </Link>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-earth-800">
            Contracts
          </h1>
          <p className="text-earth-700 text-sm mt-1">
            Set the contract document (Google Drive). Defaults apply per type; an
            override applies to one class only.
          </p>
        </div>

        {/* Defaults */}
        <h2 className="text-sm font-semibold text-earth-600 uppercase tracking-wide mb-3">
          Default per type
        </h2>
        <div className="space-y-2">
          {defaults.map((contract) => (
            <ContractRow
              key={contract.id}
              contract={contract}
              expanded={expandedId === contract.id}
              onToggle={() => toggle(contract.id)}
            />
          ))}
          {defaults.length === 0 && (
            <div className="bg-white rounded-xl border border-earth-200 text-center py-8 text-earth-700 text-sm">
              No contracts found.
            </div>
          )}
        </div>

        {/* Overrides */}
        <div className="flex items-center justify-between mt-10 mb-3">
          <h2 className="text-sm font-semibold text-earth-600 uppercase tracking-wide">
            Overrides per class{" "}
            {overrideTotal > 0 && (
              <span className="text-earth-700 normal-case font-normal">
                ({overrideTotal})
              </span>
            )}
          </h2>
          <button
            onClick={() => setAddOpen((o) => !o)}
            className="inline-flex items-center gap-1 text-sm font-medium text-olive-700 hover:text-olive-800 transition-colors"
          >
            <i className={addOpen ? "ri-close-line" : "ri-add-line"}></i>
            {addOpen ? "Cancel" : "Add override"}
          </button>
        </div>

        {/* Add override (collapsed by default) */}
        {addOpen && (
          <div className="bg-white rounded-xl border border-olive-200 p-4 mb-3">
            <label className="block text-sm font-medium text-earth-700 mb-1">
              Class-specific contract
            </label>
            <div className="flex flex-col gap-2">
              <SearchableSelect
                items={classItems}
                value={newClass}
                onChange={setNewClass}
                emptyLabel="Pick a class…"
              />
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={newFile}
                  onChange={(e) => setNewFile(e.target.value)}
                  placeholder="https://drive.google.com/file/d/FILE_ID/view"
                  className="flex-1 px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
                />
                <button
                  onClick={handleCreate}
                  disabled={
                    createOverride.isPending ||
                    !newClass ||
                    !extractDriveFileId(newFile)
                  }
                  className="px-4 py-2 bg-olive-600 hover:bg-olive-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 whitespace-nowrap"
                >
                  {createOverride.isPending ? "Creating..." : "Create override"}
                </button>
              </div>
            </div>
            {classItems.length === 0 && (
              <p className="text-[11px] text-earth-700 mt-2">
                Every active class already has an override.
              </p>
            )}
          </div>
        )}

        <div className="space-y-2">
          {overrides.map((contract) => (
            <ContractRow
              key={contract.id}
              contract={contract}
              isOverride
              expanded={expandedId === contract.id}
              onToggle={() => toggle(contract.id)}
            />
          ))}
          {overrides.length === 0 && (
            <div className="bg-white rounded-xl border border-earth-200 text-center py-8 text-earth-700 text-sm">
              No class-specific contracts yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ContractRow({
  contract,
  isOverride = false,
  expanded,
  onToggle,
}: {
  contract: AdminContract;
  isOverride?: boolean;
  expanded: boolean;
  onToggle: () => void;
}) {
  const updateContract = useUpdateContract();
  const deleteOverride = useDeleteContractOverride();
  const [input, setInput] = useState(contract.driveFileId);

  const dirty = extractDriveFileId(input) !== contract.driveFileId;
  const previewUrl = contract.driveFileId
    ? `https://drive.google.com/file/d/${contract.driveFileId}/view`
    : null;

  const handleSave = () => {
    updateContract.mutate({
      id: contract.id,
      data: { driveFileId: extractDriveFileId(input) },
    });
  };

  const affectedLabel = isOverride
    ? `class ${contract.classGroupName ?? ""}`.trim()
    : `all ${TYPE_LABEL[contract.contractType]} students`;

  return (
    <div className="bg-white rounded-xl border border-earth-200 overflow-hidden">
      {/* Collapsed summary */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-earth-50/50 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          {isOverride ? (
            <span className="px-2 py-1 bg-olive-100 text-olive-700 rounded text-xs font-semibold truncate">
              {contract.classGroupName ?? "Class"}
            </span>
          ) : (
            <span className="px-2 py-1 bg-earth-100 text-earth-700 rounded text-xs font-semibold">
              {TYPE_LABEL[contract.contractType] ?? contract.contractType}
            </span>
          )}
          {isOverride && (
            <span className="text-xs text-earth-700 truncate">
              {TYPE_LABEL[contract.contractType]}
            </span>
          )}
          {contract.driveFileId && formatUpdated(contract.updatedAt) && (
            <span className="text-xs text-earth-700 whitespace-nowrap">
              Updated {formatUpdated(contract.updatedAt)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!contract.driveFileId && (
            <span className="text-[11px] text-amber-600 inline-flex items-center gap-1">
              <i className="ri-error-warning-line"></i>
              No file
            </span>
          )}
          <i
            className={`ri-arrow-down-s-line text-earth-400 transition-transform ${
              expanded ? "rotate-180" : ""
            }`}
          ></i>
        </div>
      </button>

      {/* Expanded editor */}
      {expanded && (
        <div className="border-t border-earth-100 px-4 py-4">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <label className="block text-sm font-medium text-earth-700">
              Drive link or file ID
            </label>
            <div className="flex items-center gap-3">
              {previewUrl && (
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline text-sm inline-flex items-center gap-1"
                >
                  <i className="ri-external-link-line"></i>
                  Open current
                </a>
              )}
              {isOverride && (
                <button
                  onClick={() => deleteOverride.mutate(contract.id)}
                  disabled={deleteOverride.isPending}
                  className="text-red-500 hover:text-red-700 text-sm inline-flex items-center gap-1 disabled:opacity-50"
                  title="Remove override (class falls back to the type default)"
                >
                  <i className="ri-delete-bin-line"></i>
                  Remove
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="https://drive.google.com/file/d/FILE_ID/view"
              className="flex-1 px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
            />
            <button
              onClick={handleSave}
              disabled={updateContract.isPending || !dirty}
              className="px-4 py-2 bg-olive-600 hover:bg-olive-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 whitespace-nowrap"
            >
              {updateContract.isPending ? "Saving..." : "Save & require re-sign"}
            </button>
          </div>
          {dirty && (
            <p className="text-[11px] text-amber-600 mt-1">
              Saving replaces the document — {affectedLabel} must sign again.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminContractsPage;
